import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getSupplierPurchasesReport } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatDualCurrency, formatDate } from '../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const SupplierPurchasesReport = () => {
  const { stores } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    storeId: '',
    supplierName: '',
    startDate: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    endDate: new Date()
  });
  const [viewMode, setViewMode] = useState('summary'); // 'summary', 'by-supplier', 'by-store'

  const fetchReport = async () => {
    try {
      setLoading(true);
      const filterParams = {
        storeId: filters.storeId || null,
        supplierName: filters.supplierName || null,
        startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
        endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null
      };
      
      const data = await getSupplierPurchasesReport(filterParams);
      setReportData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching supplier purchases report:', err);
      setError('Error al cargar el reporte de compras por proveedor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const clearFilters = () => {
    setFilters({
      storeId: '',
      supplierName: '',
      startDate: new Date(new Date().getFullYear(), 0, 1),
      endDate: new Date()
    });
  };

  // Prepare data for charts
  const prepareSupplierChartData = () => {
    if (!reportData?.totals_by_supplier) return [];
    
    return Object.entries(reportData.totals_by_supplier)
      .map(([supplier, data]) => ({
        name: supplier.length > 20 ? supplier.substring(0, 20) + '...' : supplier,
        fullName: supplier,
        usd: data.usd,
        ves: data.ves,
        count: data.count
      }))
      .slice(0, 10); // Top 10 suppliers
  };

  const prepareStoreChartData = () => {
    if (!reportData?.totals_by_store) return [];
    
    return Object.entries(reportData.totals_by_store)
      .map(([storeId, data]) => ({
        name: data.store_name.length > 15 ? data.store_name.substring(0, 15) + '...' : data.store_name,
        fullName: data.store_name,
        usd: data.usd,
        ves: data.ves,
        suppliers: data.supplier_count
      }));
  };

  const preparePieChartData = () => {
    if (!reportData?.totals_by_supplier) return [];
    
    return Object.entries(reportData.totals_by_supplier)
      .map(([supplier, data]) => ({
        name: supplier,
        value: data.usd,
        ves: data.ves
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 for readability
  };

  const preparePieChartDataByUnits = () => {
    if (!reportData?.totals_by_supplier) return [];
    
    return Object.entries(reportData.totals_by_supplier)
      .map(([supplier, data]) => ({
        name: supplier,
        value: data.quantity || 0
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 for readability
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Generando reporte de compras por proveedor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img 
            src="/farmavida-logo.jpg" 
            alt="FARMAVIDA Logo" 
            className="h-12 w-auto object-contain"
          />
          <div>
            <h1 className="text-3xl font-bold text-blue-900">📦 Compras por Proveedor</h1>
            <p className="text-gray-600 mt-1">
              Análisis detallado de compras de inventario por proveedor y tienda
            </p>
          </div>
        </div>
        <button onClick={fetchReport} className="btn-primary">
          🔄 Actualizar Reporte
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🔍 Filtros</h3>
          <button onClick={clearFilters} className="btn-outline text-sm">
            Limpiar Filtros
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={filters.storeId}
              onChange={(e) => setFilters(prev => ({ ...prev, storeId: e.target.value }))}
              className="form-select"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="form-label">Proveedor</label>
            <input
              type="text"
              value={filters.supplierName}
              onChange={(e) => setFilters(prev => ({ ...prev, supplierName: e.target.value }))}
              className="form-input"
              placeholder="Buscar proveedor..."
            />
          </div>
          
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
              className="form-input"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
              className="form-input"
              dateFormat="dd/MM/yyyy"
              minDate={filters.startDate}
            />
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <button onClick={fetchReport} className="btn-primary">
            📊 Generar Reporte
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      {reportData && (
        <div className="card">
          <div className="flex border-b">
            <button
              onClick={() => setViewMode('summary')}
              className={`px-6 py-3 text-sm font-medium ${
                viewMode === 'summary'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Resumen General
            </button>
            <button
              onClick={() => setViewMode('by-supplier')}
              className={`px-6 py-3 text-sm font-medium ${
                viewMode === 'by-supplier'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🏭 Por Proveedor
            </button>
            <button
              onClick={() => setViewMode('by-store')}
              className={`px-6 py-3 text-sm font-medium ${
                viewMode === 'by-store'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🏪 Por Tienda
            </button>
          </div>

          {/* Summary View */}
          {viewMode === 'summary' && reportData && (
            <div className="p-6 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="stat-card">
                  <div className="stat-card-title">Total Compras</div>
                  <div className="stat-card-value text-green-600">
                    ${reportData.summary.grand_total_usd.toFixed(2)}
                  </div>
                  <div className="text-sm text-blue-600">
                    Bs. {reportData.summary.grand_total_ves.toFixed(2)}
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-card-title">📦 Unidades Totales</div>
                  <div className="stat-card-value text-purple-600">
                    {(reportData.summary.total_quantity || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">unidades compradas</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-card-title">Proveedores</div>
                  <div className="stat-card-value">{reportData.summary.supplier_count}</div>
                  <div className="text-sm text-gray-600">proveedores únicos</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-card-title">Compras</div>
                  <div className="stat-card-value">{reportData.summary.purchase_count}</div>
                  <div className="text-sm text-gray-600">facturas procesadas</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-card-title">Tasa Actual</div>
                  <div className="stat-card-value">Bs. {reportData.summary.current_exchange_rate.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">por $1 USD</div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top Suppliers Bar Chart */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">🏭 Top 10 Proveedores (USD)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={prepareSupplierChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                      <Tooltip 
                        formatter={(value, name) => [`$${value.toFixed(2)}`, 'Compras USD']}
                        labelFormatter={(label, payload) => 
                          payload?.[0]?.payload?.fullName || label
                        }
                      />
                      <Bar dataKey="usd" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Suppliers Distribution Pie Chart by USD */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">📊 Distribución por Proveedor (USD)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={preparePieChartData()}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${percent > 5 ? name.substring(0, 10) : ''} ${(percent * 100).toFixed(1)}%`}
                      >
                        {preparePieChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name, props) => [
                          `$${value.toFixed(2)}`,
                          props.payload.name
                        ]}
                        labelFormatter={(value) => `Proveedor: ${value}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Suppliers Distribution Pie Chart by Units */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">📦 Distribución por Unidades</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={preparePieChartDataByUnits()}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${percent > 5 ? name.substring(0, 10) : ''} ${(percent * 100).toFixed(1)}%`}
                      >
                        {preparePieChartDataByUnits().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 50}, 65%, 55%)`} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name, props) => [
                          `${value.toLocaleString()} unidades`,
                          props.payload.name
                        ]}
                        labelFormatter={(value) => `Proveedor: ${value}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Suppliers Table */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">🏆 Ranking de Proveedores</h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Posición</th>
                        <th>Proveedor</th>
                        <th>Total USD</th>
                        <th>Total VES</th>
                        <th>Unidades</th>
                        <th>Compras</th>
                        <th>Promedio por Compra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(reportData.totals_by_supplier).map(([supplier, data], index) => (
                        <tr key={supplier}>
                          <td>
                            <span className={`font-bold ${index < 3 ? 'text-yellow-600' : 'text-gray-600'}`}>
                              #{index + 1}
                            </span>
                          </td>
                          <td>
                            <span className="font-medium text-blue-600">{supplier}</span>
                          </td>
                          <td>
                            <span className="font-mono text-green-600">
                              ${data.usd.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-mono text-blue-600">
                              Bs. {data.ves.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-medium">
                              {data.quantity || 0}
                            </span>
                          </td>
                          <td>
                            <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {data.count}
                            </span>
                          </td>
                          <td>
                            <span className="text-sm text-gray-600">
                              ${(data.usd / data.count).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* By Supplier View */}
          {viewMode === 'by-supplier' && reportData?.detailed_data && (
            <div className="p-6 space-y-6">
              {Object.entries(reportData.detailed_data).map(([supplier, supplierData]) => (
                <div key={supplier} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-blue-900">🏭 {supplier}</h4>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        ${supplierData.total_usd.toFixed(2)}
                      </div>
                      <div className="text-sm text-blue-600">
                        Bs. {supplierData.total_ves.toFixed(2)}
                      </div>
                      <div className="text-sm text-purple-600">
                        {supplierData.total_quantity || 0} unidades
                      </div>
                    </div>
                  </div>

                  {/* Stores for this supplier */}
                  <div className="space-y-3">
                    {Object.entries(supplierData.stores).map(([storeId, storeData]) => (
                      <div key={storeId} className="bg-gray-50 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-800">🏪 {storeData.store_name}</span>
                          <div className="text-right">
                            <span className="text-green-600 font-medium">${storeData.total_usd.toFixed(2)}</span>
                            <span className="text-blue-600 text-sm ml-2">Bs. {storeData.total_ves.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          {storeData.purchase_count} compra{storeData.purchase_count !== 1 ? 's' : ''} • 
                          <span className="text-purple-600 font-medium"> {storeData.total_quantity || 0} unidades</span> • 
                          Promedio: ${(storeData.total_usd / storeData.purchase_count).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* By Store View */}
          {viewMode === 'by-store' && reportData?.totals_by_store && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(reportData.totals_by_store).map(([storeId, storeData]) => (
                  <div key={storeId} className="card">
                    <div className="card-header">
                      <h4 className="card-title">🏪 {storeData.store_name}</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          ${storeData.usd.toFixed(2)}
                        </div>
                        <div className="text-blue-600">
                          Bs. {storeData.ves.toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 text-sm text-gray-600">
                        <div className="text-center">
                          <span className="font-medium">Proveedores</span>
                          <div className="text-lg font-bold text-gray-800">
                            {storeData.supplier_count}
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="font-medium">Compras</span>
                          <div className="text-lg font-bold text-gray-800">
                            {storeData.count}
                          </div>
                        </div>
                        <div className="text-center">
                          <span className="font-medium">📦 Unidades</span>
                          <div className="text-lg font-bold text-purple-600">
                            {(storeData.quantity || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-center text-sm text-gray-500">
                        Promedio por compra: ${(storeData.usd / storeData.count).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Data Message */}
      {reportData && Object.keys(reportData.detailed_data || {}).length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay compras registradas
          </h3>
          <p className="text-gray-600 mb-4">
            No se encontraron compras de inventario en el período seleccionado.
          </p>
        </div>
      )}
    </div>
  );
};

export default SupplierPurchasesReport;